import type { ApiNode } from '@goalbuddy/client';
import { List, Plus } from 'lucide-react';
import { Link } from 'react-router';
import { Button, Card, Empty, PageHeader, Spinner } from '@/components/ui';
import { areaColor } from '@/lib/area';
import { useNodes } from '@/lib/hooks';
import { ancestorsOf } from '@/lib/tree';
import { openNewNode } from '@/lib/ui-events';

/** Every list in one place, grouped by area, with the number of open items. */
export function ListsOverviewPage() {
  const { data: all, isLoading } = useNodes({ include_done: true, limit: 1000 });
  if (isLoading) return <Spinner />;
  const nodes = all ?? [];
  const lists = nodes.filter((n) => n.type === 'list' && n.status !== 'dropped');
  const groups = new Map<string, ApiNode[]>();
  for (const l of lists) {
    const area = ancestorsOf(nodes, l.id).find((a) => a.type === 'area')?.title ?? 'No area';
    groups.set(area, [...(groups.get(area) ?? []), l]);
  }
  const open = (id: number) =>
    nodes.filter((n) => n.parent_id === id && n.status !== 'done' && n.status !== 'dropped').length;
  return (
    <div className="space-y-5">
      <PageHeader
        title="Lists"
        subtitle="Collections without an outcome: wishlists, someday, reference. Anything with an outcome belongs in a project."
      >
        <Button onClick={() => openNewNode('list')}>
          <Plus size={14} /> New list
        </Button>
      </PageHeader>
      {lists.length === 0 && (
        <Empty
          icon={<List size={20} />}
          action={
            <Button size="sm" onClick={() => openNewNode('list')}>
              <Plus size={13} /> Create your first list
            </Button>
          }
        >
          No lists yet.
        </Empty>
      )}
      {[...groups.entries()].map(([area, ls]) => (
        <section key={area}>
          <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-fg">{area}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {ls.map((l) => {
              const color = areaColor(nodes, l.id);
              const n = open(l.id);
              return (
                <Link key={l.id} to={`/node/${l.id}`}>
                  <Card
                    className="flex h-full items-center gap-3 p-4 transition-shadow hover:shadow-md"
                    style={color ? { borderLeft: `4px solid ${color}` } : undefined}
                  >
                    <List size={18} className="shrink-0 text-teal-600" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{l.title}</div>
                      <div className="text-xs text-muted-fg">
                        {n} open {n === 1 ? 'item' : 'items'}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
